import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Container,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: theme.shadows[3],
  marginBottom: theme.spacing(4),
}));

const Payments = ({ unitCode, companyCode, unitType, companyName, userId, isPureVeg }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const urlUnitCode = queryParams.get('unit') || location.state?.unitCode || '';
  const effectiveUnitCode = urlUnitCode || unitCode;
  const filterType = queryParams.get('filterType') || 'year';

  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const isRestaurant = unitType?.toLowerCase() === 'restaurant';
  const isHotel = unitType?.toLowerCase() === 'hotel';
  const isFoodcourt = unitType?.toLowerCase() === 'foodcourt';
  const itemType = isRestaurant ? 'Table' : isHotel ? 'Room' : 'Customer';
  const idField = isRestaurant ? 'tableId' : isHotel ? 'roomId' : 'customerId';

  useEffect(() => {
    const fetchPayments = async () => {
      if (!effectiveUnitCode) {
        setError('Unit code is missing. Please specify a unit code in the URL (e.g., ?unit=UNIT123) or ensure you are logged in with a valid unit.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError('');

        const ordersQuery = query(
  collection(db, 'orders'),
  where('unitCode', '==', effectiveUnitCode)
);

const ordersSnapshot = await getDocs(ordersQuery);

/*const ordersMap = {};
ordersSnapshot.docs.forEach(doc => {
  const data = doc.data();
  ordersMap[data.orderId] = data; //correct mapping
});*/

const ordersMap = {};

ordersSnapshot.docs.forEach(docSnap => {
  const data = docSnap.data();

  if (data.orderId) {
    ordersMap[data.orderId] = {
      id: docSnap.id,
      ...data,
    };
  }
});

        // Fetch payments
        const paymentsQuery = query(
          collection(db, 'payments'),
          where('unitCode', '==', effectiveUnitCode)
        );
        const paymentsSnapshot = await getDocs(paymentsQuery);
        const paymentsData = paymentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        //setPayments(paymentsData);
       /*const mergedPayments = paymentsData.map(payment => {
  const order = ordersMap[payment.orderId];

  return {
    ...payment,
    orderAmount: order?.grandTotal || 0,
    orderStatus: order?.orderStatus || 'Unknown'
  };
});

setPayments(mergedPayments);*/
const mergedPayments = paymentsData
  .map(payment => {
    const order = ordersMap[payment.orderId];

    return {
      ...payment,
      orderAmount: order?.grandTotal || 0,
      orderStatus: order?.orderStatus || 'Unknown',
      orderCreatedAt: order?.createdAt || null,
    };
  })
  .filter(payment => {
    if (!payment.orderCreatedAt?.toDate) return false;

    const paymentDate = new Date(payment.orderCreatedAt.toDate());

    const now = new Date();

    if (filterType === "today") {
      return (
        paymentDate.getDate() === now.getDate() &&
        paymentDate.getMonth() === now.getMonth() &&
        paymentDate.getFullYear() === now.getFullYear()
      );
    }

    if (filterType === "month") {
      return (
        paymentDate.getMonth() === now.getMonth() &&
        paymentDate.getFullYear() === now.getFullYear()
      );
    }

    // year
    return paymentDate.getFullYear() === now.getFullYear();
  });

setPayments(mergedPayments);

        console.log('UnitPayments: Fetched data:', {
          unitCode: effectiveUnitCode,
          companyCode,
          userId,
          payments: paymentsData,
        });

        if (paymentsData.length === 0) {
          setError(`No payments found for unit ${effectiveUnitCode}.`);
        }
        setIsLoading(false);
      } catch (error) {
        console.error('UnitPayments: Error fetching payments:', { error, unitCode: effectiveUnitCode, userId });
        setError('Failed to load payments. Check Firestore permissions or data.');
        setIsLoading(false);
      }
    };

    fetchPayments();
  }, [effectiveUnitCode, companyCode, userId]);

  const handleBackToDashboard = () => {
    console.log('UnitPayments: Navigating to /manager/home', { unitCode: effectiveUnitCode, userId });
    const queryParams = new URLSearchParams({
      unit: effectiveUnitCode,
      name: companyName,
      type: unitType,
      isPureVeg: isPureVeg.toString(),
    });
    navigate(`/unit_manager/home?${queryParams.toString()}`, {
      state: { unitCode: effectiveUnitCode, companyCode, companyName, unitType, isPureVeg },
    });
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading payments...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleBackToDashboard}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 'bold', color: 'primary.main', mb: 4, textAlign: 'center' }}
      >
        Payments ({filterType.toUpperCase()}) - {companyName} ({effectiveUnitCode})
      </Typography>

      <StyledCard>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.light' }}>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>{itemType} ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Order ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Amount (₹)</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Payment Method</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Date</TableCell>
                {isFoodcourt && (
                  <>
                    <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Customer Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Customer Mobile</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.length > 0 ? (
                payments.map((payment, index) => (
                  <TableRow
                    key={payment.id}
                    sx={{
                      bgcolor: index % 2 === 0 ? '#fafafa' : '#fff',
                      '&:hover': { bgcolor: '#f5f5f5' },
                    }}
                  >
                    <TableCell>{payment[idField] || 'N/A'}</TableCell>
                    <TableCell>{payment.orderId || 'N/A'}</TableCell>
                    {/*<TableCell>{payment.amount?.toFixed(2) || '0.00'}</TableCell>*/}
                    {/*<TableCell>
  {payment.orderStatus === 'Paid'
    ? `₹ ${payment.orderAmount?.toFixed(2)}`
    : `₹ ${payment.amount?.toFixed(2)}`
  }
</TableCell>*/}
<TableCell>
  {payment.orderStatus === 'Paid'
    ? `₹ ${Number(payment.orderAmount || 0).toFixed(2)}`
    : `₹ ${Number(payment.amount || 0).toFixed(2)}`
  }
</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={
                          payment.paymentMethod === 'Cash' ? 'success.main' :
                          payment.paymentMethod === 'Card' ? 'info.main' :
                          payment.paymentMethod === 'UPI' ? 'primary.main' :
                          'text.primary'
                  
                        }
                      >
                        {payment.paymentMethod || 'Online'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {payment.date?.toDate().toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      }) || 'N/A'}
                    </TableCell>
                    {isFoodcourt && (
                      <>
                        <TableCell>{payment.customerName || 'N/A'}</TableCell>
                        <TableCell>{payment.customerMobile || 'N/A'}</TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isFoodcourt ? 7 : 5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No payments available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </StyledCard>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleBackToDashboard}
          sx={{ minWidth: 150 }}
        >
          Back to Dashboard
        </Button>
      </Box>
    </Container>
  );
};

export default Payments;